import { Skeleton, Tabs, Typography } from 'antd';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { LeadCard } from '@app/components/LeadCard';
import { VisitsHistory } from '@app/components/VisitsHistory';
import { DESKTOP } from '@app/constants';
import { PREFIX } from '@app/constants/appPrefix';
import {
  useClientsAccessRights,
  useMedicalDocumentsAccessRights,
} from '@app/hooks';
import { EditClientPage } from '@app/pages/EditClientPage/ui/EditClientPage.main';

import {
  useAddClient,
  useAddClientEffects,
} from '@pages/Clients/AddClient/hooks';

import { MedicalConsultationHistory } from './components';

import type { FC } from 'react';

import '@pages/Clients/EditClient/styles/EditClient.desktop.scss';
import '@pages/Clients/EditClient/styles/EditClient.tablet.scss';

const { Title, Text } = Typography;

export const EditClient: FC = () => {
  const navigate = useNavigate();
  const {
    data,
    clientId,
    checkIsDataUpdated,
    fetchData,
    initSelectedTags,
    leadId,
  } = useAddClient();

  useAddClientEffects({ fetchData, initSelectedTags });

  const { hasUserRightToEdit } = useClientsAccessRights();
  const { hasUserRightToViewMedicalDocuments } =
    useMedicalDocumentsAccessRights();
  const [searchParams] = useSearchParams();
  const initialTab = Number(searchParams.get('key'));

  const [activeKey, setActiveKey] = useState('1');
  const isDesktop = innerWidth >= DESKTOP;

  useEffect(() => {
    setActiveKey(`${initialTab}`);
  }, [initialTab]);

  const handleTabClick = (key: string) => {
    const leadId = Number(searchParams.get('leadId'));
    const leadIdParam = leadId ? `&leadId=${leadId}` : '';
    if (data.Id) {
      checkIsDataUpdated({
        path: `/clients/edit/?id=${data.Id}&key=${key}${leadIdParam}`,
        navigate,
      });
    } else {
      checkIsDataUpdated({
        path: `/clients/card/?id=${data.Id}${leadIdParam}`,
        navigate,
      });
    }
  };

  const tabs = useMemo(() => {
    return [
      {
        label: 'Сообщения',
        key: '1',
        className: `${PREFIX}edit-client__lead-card-pane`,
        children: (
          <div className={`${PREFIX}edit-client__lead-card-tab`}>
            <LeadCard
              leadId={leadId || 0}
              showLeadList={false}
              showGoToLeadAction
              knownClientId={clientId}
            />
          </div>
        ),
      },
      ...(hasUserRightToEdit && !data?.IsDeleted
        ? [
            {
              label: 'Редактировать',
              key: '2',
              children: <EditClientPage />,
            },
          ]
        : []),
      {
        label: 'История визитов',
        key: '3',
        children: <VisitsHistory clientId={clientId ?? null} />,
      },
      ...(hasUserRightToViewMedicalDocuments
        ? [
            {
              label: 'Мед. консультации',
              key: '5',
              children: <MedicalConsultationHistory clientId={clientId} />,
            },
          ]
        : []),
    ];
  }, [
    data,
    hasUserRightToEdit,
    hasUserRightToViewMedicalDocuments,
    leadId,
    clientId,
  ]);

  if (!data && clientId) {
    return <Skeleton active />;
  }

  return (
    <div className={`${PREFIX}edit-client`}>
      <>
        <div className="ant-tabs-nav">
          <Title level={4} className="mg-0">
            Клиент
          </Title>
          <Text strong ellipsis={{ tooltip: true }}>
            {`${data.NameLast || ''} ${data.NameFirst || ''}`.trim()}
          </Text>
        </div>
        <Tabs
          className={`${PREFIX}global-tab-styles`}
          type={isDesktop ? 'line' : 'card'}
          tabPosition={isDesktop ? 'left' : 'top'}
          items={tabs}
          onTabClick={handleTabClick}
          activeKey={activeKey}
        />
      </>
    </div>
  );
};
